import { useEffect, useMemo, useRef, useState } from 'react'
import type { JSX } from 'react'

import {
  Button,
  CopyIcon,
  EmptyState,
  Flex,
  IconButton,
  Input,
  Notice,
  ScrollArea,
  SearchIcon,
  Skeleton,
  toast,
} from '@buffer-mono/popcorn'

import { SidePanel } from '~publish/components/Composer/components/SidePanel'
import { TemplateDetailsView } from '~publish/components/Composer/components/TemplatesSidePanel/TemplateDetailsView'
import { TemplateCard } from '~publish/components/TemplateCard'
import { TemplateDetails } from '~publish/components/TemplateDetails'
import { usePublicPostTemplates } from '~publish/pages/Create/Templates/hooks/usePublicPostTemplates'
import type { PublicPostTemplate } from '~publish/pages/Create/Templates/hooks/usePublicPostTemplates'

import styles from './PublicComposerPage.module.css'

export const PUBLIC_TEMPLATES_PANEL_ID = 'public-composer-templates'

function PublicTemplateDetails({
  template,
  onBack,
  onUseTemplate,
}: {
  template: PublicPostTemplate
  onBack: () => void
  onUseTemplate: (template: PublicPostTemplate) => void
}): JSX.Element {
  const backButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    backButtonRef.current?.focus()
  }, [])

  const copyTemplate = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(template.body)
      toast.success('Template copied to clipboard')
    } catch {
      toast.error('Failed to copy template to clipboard')
    }
  }

  return (
    <TemplateDetailsView
      template={template}
      onBack={onBack}
      backButtonRef={backButtonRef}
      icon={
        <TemplateDetails.IconSwatch>
          <TemplateDetails.Icon />
        </TemplateDetails.IconSwatch>
      }
      actions={
        <>
          <IconButton
            label="Copy template"
            variant="tertiary"
            onClick={() => void copyTemplate()}
          >
            <CopyIcon />
          </IconButton>
          <Button onClick={() => onUseTemplate(template)}>Use Template</Button>
        </>
      }
      metadata={<TemplateDetails.Metadata />}
    />
  )
}

function LoadingTemplates(): JSX.Element {
  return (
    <Flex direction="column" gap="md" className={styles.templatesState}>
      <Skeleton height={100} />
      <Skeleton height={100} />
      <Skeleton height={100} />
      <Skeleton height={100} />
    </Flex>
  )
}

export function PublicInitialTemplate({
  templateId,
  onHandled,
  onUseTemplate,
}: {
  templateId: string
  onHandled: () => void
  onUseTemplate: (template: PublicPostTemplate) => void
}): null {
  const { loading, templates, error } = usePublicPostTemplates()
  const handledTemplateIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (loading || error || handledTemplateIdRef.current === templateId) {
      return
    }

    handledTemplateIdRef.current = templateId
    const template = templates.find(({ id }) => id === templateId)
    onHandled()

    if (!template) {
      toast.error('Template is unavailable')
      return
    }

    onUseTemplate(template)
  }, [error, loading, onHandled, onUseTemplate, templateId, templates])

  return null
}

export function PublicTemplatesSidePanel({
  onUseTemplate,
}: {
  onUseTemplate: (template: PublicPostTemplate) => void
}): JSX.Element {
  const { loading, templates, error } = usePublicPostTemplates()
  const [query, setQuery] = useState('')
  const [selectedTemplate, setSelectedTemplate] =
    useState<PublicPostTemplate | null>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!selectedTemplate)
      searchInputRef.current?.focus({ preventScroll: true })
  }, [selectedTemplate])

  const filteredTemplates = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase()
    if (!normalizedQuery) return templates

    return templates.filter((template) =>
      [
        template.title,
        template.description,
        template.body,
        template.type,
        template.goal,
        ...template.tags,
      ].some((value) => value.toLowerCase().includes(normalizedQuery)),
    )
  }, [query, templates])

  return (
    <SidePanel id={PUBLIC_TEMPLATES_PANEL_ID} open aria-label="Templates">
      {selectedTemplate ? (
        <PublicTemplateDetails
          template={selectedTemplate}
          onBack={() => setSelectedTemplate(null)}
          onUseTemplate={onUseTemplate}
        />
      ) : (
        <>
          <SidePanel.Header className={styles.templatesHeader}>
            <SidePanel.Heading>Templates</SidePanel.Heading>
            <Input
              ref={searchInputRef}
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search templates…"
              aria-label="Search templates"
              prefix={<SearchIcon />}
              className={styles.templatesSearch}
            />
          </SidePanel.Header>
          <SidePanel.Body asChild>
            <ScrollArea visibility="scroll" scrollbars="vertical">
              {loading ? <LoadingTemplates /> : null}
              {!loading && error && templates.length === 0 ? (
                <Notice variant="error" className={styles.templatesState}>
                  <Notice.Heading>Templates are unavailable</Notice.Heading>
                  <Notice.Text>Please try again in a moment.</Notice.Text>
                </Notice>
              ) : null}
              {!loading && error && templates.length > 0 ? (
                <Notice variant="warning" className={styles.templatesNotice}>
                  Some templates could not be loaded.
                </Notice>
              ) : null}
              {!loading && (!error || templates.length > 0) ? (
                filteredTemplates.length > 0 ? (
                  <div className={styles.templatesList}>
                    {filteredTemplates.map((template) => (
                      <TemplateCard
                        key={template.id}
                        template={template}
                        className={styles.templateCard}
                      >
                        <TemplateCard.Clickable
                          aria-label={`Open template ${template.title}`}
                          onClick={() => setSelectedTemplate(template)}
                        />
                        <TemplateCard.Icon size="md" />
                        <TemplateCard.Title
                          as="h3"
                          size="xs"
                          className={styles.templateCardTitle}
                        />
                        <TemplateCard.Description maxLines={3} />
                        <TemplateCard.Tags />
                      </TemplateCard>
                    ))}
                  </div>
                ) : (
                  <EmptyState size="small" className={styles.templatesState}>
                    <EmptyState.Icon>
                      <SearchIcon />
                    </EmptyState.Icon>
                    <EmptyState.Heading size="small">
                      No templates found
                    </EmptyState.Heading>
                    <EmptyState.Description>
                      Try a different search.
                    </EmptyState.Description>
                    <EmptyState.Actions>
                      <Button variant="tertiary" onClick={() => setQuery('')}>
                        Clear search
                      </Button>
                    </EmptyState.Actions>
                  </EmptyState>
                )
              ) : null}
            </ScrollArea>
          </SidePanel.Body>
        </>
      )}
    </SidePanel>
  )
}
